import { useState, useEffect, useCallback, useMemo } from 'react'
import { useAuth } from '../../../../auth'
import {
  searchDefects,
  searchNearMissReports,
  searchAudits,
  searchInspections,
  searchWorkPermits,
} from '../../../core/_requests'
import type {
  QHSEDefectDto,
  QHSENearMissReportDto,
  AuditDto,
  InspectionDto,
  QHSEWorkPermitDto,
} from '../../../core/_models'
import type { QhseDashboardData, QhseDerivedKpis } from '../types'

const normalizeArray = <T>(data: any): T[] => {
  if (Array.isArray(data)) return data
  if (data?.content) return data.content
  return []
}

export function useQhseDashboard() {
  const { currentUser } = useAuth()
  const roleId = Number((currentUser as any)?.role?.id ?? 0)

  const [loading, setLoading] = useState(true)
  const [defects, setDefects] = useState<QHSEDefectDto[]>([])
  const [nearMisses, setNearMisses] = useState<QHSENearMissReportDto[]>([])
  const [audits, setAudits] = useState<AuditDto[]>([])
  const [inspections, setInspections] = useState<InspectionDto[]>([])
  const [workPermits, setWorkPermits] = useState<QHSEWorkPermitDto[]>([])

  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const results = await Promise.allSettled([
        searchDefects({ activeOnly: true }),
        searchNearMissReports({}),
        searchAudits({}),
        searchInspections({}),
        searchWorkPermits({}),
      ])

      const [defectRes, nearMissRes, auditRes, inspRes, wpRes] = results

      if (defectRes.status === 'fulfilled') setDefects(normalizeArray<QHSEDefectDto>(defectRes.value))
      if (nearMissRes.status === 'fulfilled') setNearMisses(normalizeArray<QHSENearMissReportDto>(nearMissRes.value))
      if (auditRes.status === 'fulfilled') setAudits(normalizeArray<AuditDto>(auditRes.value))
      if (inspRes.status === 'fulfilled') setInspections(normalizeArray<InspectionDto>(inspRes.value))
      if (wpRes.status === 'fulfilled') setWorkPermits(normalizeArray<QHSEWorkPermitDto>(wpRes.value))
    } catch {
      // silent — dashboard gracefully shows empty states
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchAll() }, [fetchAll])

  // Compute YTD for near misses
  const nearMissesYTD = useMemo(() => {
    const currentYear = new Date().getFullYear().toString()
    return nearMisses.filter((nm) => {
      const d = nm.dateOfOccurrence || nm.dateIssued || nm.createdAt
      return d ? d.startsWith(currentYear) : false
    }).length
  }, [nearMisses])

  const data: QhseDashboardData = useMemo(() => ({
    defects,
    nearMisses,
    audits,
    inspections,
    workPermits,
  }), [defects, nearMisses, audits, inspections, workPermits])

  const kpis: QhseDerivedKpis = useMemo(() => ({
    openDefects: defects.filter((d) => d.status === 'SUBMIT' || d.status === 'SAVE').length,
    nearMissesYTD,
    openAudits: audits.filter((a) => !a.isFinalized).length,
    openWorkPermits: workPermits.filter((wp) => wp.status === 'SUBMIT' || wp.status === 'SAVE').length,
    pendingInspections: inspections.filter((i) => !i.isFinalized).length,
    expiringCerts: 0, // placeholder — no bulk expiry endpoint
  }), [defects, nearMissesYTD, audits, workPermits, inspections])

  return {
    loading,
    data,
    kpis,
    refresh: fetchAll,
  }
}
